import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import { api } from '@/api/apiClient';
import { createPageUrl } from '@/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, Mail, Lock, AlertCircle, User, Fingerprint, Moon, Sun } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useTheme } from '@/hooks/useTheme';
import { loadFeatureFlags } from '@/components/utils/featureFlags';
import { toast } from 'sonner';

export default function Login() {
  const { isDark, toggleTheme } = useTheme();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('signin');
  const [isActivation, setIsActivation] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [requires2FA, setRequires2FA] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [hasPasskeys, setHasPasskeys] = useState(false);
  const [checkingPasskeys, setCheckingPasskeys] = useState(false);
  const [canRegister, setCanRegister] = useState(true);

  useEffect(() => {
    loadFlags();
    const params = new URLSearchParams(location.search);
    const mode = params.get('mode');
    const activation = params.get('activation');
    const pendingToken = sessionStorage.getItem('enrollmentToken');
    if (mode === 'register') {
      setActiveTab('register');
    }
    if (activation === '1' || pendingToken) {
      setIsActivation(true);
      setActiveTab('register');
    }
    if (mode === 'register') {
      setActiveTab('register');
    }
  }, []);

  const loadFlags = async () => {
    try {
      const flags = await loadFeatureFlags();
      // Allow registration if either flag is enabled
      const registrationEnabled = flags.allow_company_registration || flags.allow_individual_registration;
      setCanRegister(registrationEnabled);
    } catch (err) {
      console.error('Failed to load feature flags:', err);
    }
  };

  useEffect(() => {
    const enrollmentEmail = sessionStorage.getItem('enrollmentEmail');
    if (enrollmentEmail) {
      setEmail(enrollmentEmail);
      setActiveTab('register');
    }
  }, []);

  const redirectUser = async (user) => {
    // Check if there's an enrollment token to claim (from session storage)
    const enrollmentToken = sessionStorage.getItem('enrollmentToken');
    const enrollmentEmail = sessionStorage.getItem('enrollmentEmail');

    if (enrollmentToken && enrollmentEmail) {
      // Verify user email matches enrollment email
      if (user.email?.toLowerCase() === enrollmentEmail.toLowerCase()) {
        try {
          const claimResponse = await api.apiClient.post(
            '/public/enrollment-token/claim',
            { token: enrollmentToken }
          );

          if (claimResponse.data.success) {
            // Clear session storage
            sessionStorage.removeItem('enrollmentToken');
            sessionStorage.removeItem('enrollmentEmail');

            // Update local user context
            const updatedUser = {
              ...user,
              employerId: claimResponse.data.employerId,
              employeeId: claimResponse.data.employeeId
            };
            localStorage.setItem('user', JSON.stringify(updatedUser));

            // Navigate based on onboarding status
            const onboardingNeeded = claimResponse.data.needsOnboarding || (claimResponse.data.lastOnboardingStep && claimResponse.data.lastOnboardingStep !== 'done');
            if (onboardingNeeded) {
              globalThis.location.href = createPageUrl('EmployeeOnboarding');
            } else {
              const step = claimResponse.data.lastEnrollmentStep;
              if (!step || step === 'intro') {
                globalThis.location.href = createPageUrl('EnrollmentIntro');
              } else if (step === 'select') {
                globalThis.location.href = createPageUrl('BenefitsCatalog');
              } else if (step === 'review') {
                globalThis.location.href = createPageUrl('EnrollmentReview');
              } else if (step === 'sign') {
                globalThis.location.href = createPageUrl('EnrollmentSign');
              } else {
                globalThis.location.href = createPageUrl('EmployeeDashboard');
              }
            }
            return;
          }
        } catch (claimError) {
          console.error('Failed to claim enrollment token after login:', claimError);
          // Clear invalid token and continue with normal redirect
          sessionStorage.removeItem('enrollmentToken');
          sessionStorage.removeItem('enrollmentEmail');
        }
      } else {
        // Email mismatch - clear token and show error
        sessionStorage.removeItem('enrollmentToken');
        sessionStorage.removeItem('enrollmentEmail');
        toast.error(`This invitation is for ${enrollmentEmail}, but you're logged in as ${user.email}`);
      }
    }

    // Check for return URL
    const returnUrl = api.auth.getReturnUrl();
    if (returnUrl && returnUrl !== '/') {
      globalThis.location.href = returnUrl;
      return;
    }

    // Redirect based on role
    if (user.user_role === 'admin') {
      globalThis.location.href = createPageUrl('AdminDashboard');
    } else if (user.user_role === 'sub_admin') {
      globalThis.location.href = createPageUrl('EmployerDashboard');
    } else if (user.user_role === 'employer_hr') {
      if (user.employer_id) {
        globalThis.location.href = createPageUrl('EmployerDashboard');
      } else {
        globalThis.location.href = createPageUrl('CompanyOnboarding');
      }
    } else if (user.user_role === 'employee') {
      try {
        const employeeRecords = user.employee_id ? await api.entities.Employee.filter({ id: user.employee_id }) : await api.entities.Employee.filter({ user_email: user.email });
        const emp = employeeRecords?.[0];
        if (!emp) {
          globalThis.location.href = createPageUrl('InviteCodeEntry');
          return;
        }
        if (!emp.profile_confirmed) {
          globalThis.location.href = createPageUrl('EmployeeOnboarding');
          return;
        }
        const step = emp.last_enrollment_step;
        if (!step || step === 'intro') {
          globalThis.location.href = createPageUrl('EnrollmentIntro');
        } else if (step === 'select') {
          globalThis.location.href = createPageUrl('Enrollment');
        } else if (step === 'review') {
          globalThis.location.href = createPageUrl('EnrollmentReview');
        } else if (step === 'sign') {
          globalThis.location.href = createPageUrl('EnrollmentSign');
        } else {
          globalThis.location.href = createPageUrl('EmployeeDashboard');
        }
      } catch (err) {
        console.error('Employee resume routing failed:', err);
        globalThis.location.href = createPageUrl('EmployeeDashboard');
      }
    } else {
      // No role yet - go to role selection
      globalThis.location.href = createPageUrl('RoleSelection');
    }
  };

  const checkForPasskeys = async (emailValue) => {
    if (!emailValue || !emailValue.includes('@')) {
      setHasPasskeys(false);
      return;
    }
    
    setCheckingPasskeys(true);
    try {
      console.log('Checking for passkeys for:', emailValue);
      
      // Check if browser supports WebAuthn
      if (!globalThis.PublicKeyCredential) {
        console.log('Browser does not support WebAuthn');
        setHasPasskeys(false);
        setCheckingPasskeys(false);
        return;
      }
      
      // Check if user has passkeys registered
      const response = await fetch('/api/passkey/authenticate/options', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: emailValue })
      });
      
      console.log('Passkey check response:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Has passkeys:', data);
        setHasPasskeys(true);
      } else if (response.status === 404) {
        // User doesn't have passkeys or doesn't exist (don't reveal which)
        console.log('No passkeys available for this email');
        setHasPasskeys(false);
      } else {
        const errorData = await response.json();
        console.log('Error checking passkeys:', errorData);
        setHasPasskeys(false);
      }
    } catch (err) {
      console.error('Error checking for passkeys:', err);
      setHasPasskeys(false);
    } finally {
      setCheckingPasskeys(false);
    }
  };

  const handleDiscoverablePasskeyLogin = async () => {
    setError('');
    setLoading(true);

    try {
      // Get authentication options for discoverable credentials (no email needed)
      const optionsResponse = await fetch('/api/passkey/authenticate/discoverable', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!optionsResponse.ok) {
        throw new Error('Failed to get authentication options');
      }

      const options = await optionsResponse.json();

      // Start authentication - authenticator will show available passkeys
      const credential = await startAuthentication(options);

      // Send credential to server for verification
      const verifyResponse = await fetch('/api/passkey/authenticate/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential })
      });

      if (!verifyResponse.ok) {
        throw new Error('Failed to verify passkey');
      }

      const response = await verifyResponse.json();
      console.log('Passkey login response:', response);
      
      // Store tokens in API client
      if (response.accessToken) {
        localStorage.setItem('accessToken', response.accessToken);
      }
      if (response.refreshToken) {
        localStorage.setItem('refreshToken', response.refreshToken);
      }
      
      // Login successful - redirect
      await redirectUser(response.user);
    } catch (err) {
      console.error('Passkey login error:', err);
      if (err.name === 'NotAllowedError') {
        setError('Passkey authentication was cancelled');
      } else {
        setError('Failed to sign in with passkey. You can use email and password below instead.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignIn = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await api.auth.login(email, password, twoFactorCode || undefined);
      
      // Check if 2FA is required
      if (response.requires2FA) {
        setRequires2FA(true);
        setError('');
      } else {
        // Login successful
        await redirectUser(response);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.error || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Register without role - will be selected after
      const effectiveName = fullName || email;
      await api.auth.register(email, password, effectiveName);

      // Immediately sign in to continue the invite flow
      const loginResponse = await api.auth.login(email, password);
      await redirectUser(loginResponse);
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const registerCta = isActivation ? 'Activate Account' : 'Create Account';
  const registerLoading = isActivation ? 'Activating...' : 'Creating account...';

  return (
    <div className="min-h-screen bg-background text-foreground transition-colors duration-300">
      {/* Header - matching Welcome page */}
      <header className="bg-background/80 backdrop-blur-sm border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button 
            onClick={() => { globalThis.location.href = '/'; }}
            className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          >
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-foreground">BenefitsPro</span>
          </button>
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex items-center justify-center px-6 py-16">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardContent className="pt-6">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="signin">Sign In</TabsTrigger>
                <TabsTrigger value="register" disabled={!canRegister}>{isActivation ? 'Activate' : 'Create Account'}</TabsTrigger>
              </TabsList>

              {/* Sign In Tab */}
              <TabsContent value="signin">
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-foreground">Welcome Back</h2>
                    <p className="text-muted-foreground mt-1">Sign in to access your benefits</p>
                  </div>

                  {error && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {/* Passkey Login - No email needed! */}
                  {globalThis.PublicKeyCredential && !requires2FA && (
                    <div className="mb-4">
                      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <Fingerprint className="w-5 h-5 text-blue-600" />
                          <p className="font-semibold text-blue-900">Passwordless Sign In</p>
                        </div>
                        <Button
                          type="button"
                          onClick={handleDiscoverablePasskeyLogin}
                          disabled={loading}
                          className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white font-semibold py-6 text-lg"
                        >
                          {loading ? (
                            <>
                              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                              Authenticating...
                            </>
                          ) : (
                            <>
                              <Fingerprint className="w-5 h-5 mr-2" />
                              Sign In with Passkey
                            </>
                          )}
                        </Button>
                        <p className="text-xs text-blue-700 mt-2 text-center">
                          Use Face ID, Touch ID, or Windows Hello - no email needed
                        </p>
                      </div>
                      
                      <div className="relative my-6">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-slate-200"></div>
                        </div>
                        <div className="relative flex justify-center text-xs uppercase">
                          <span className="bg-white px-2 text-slate-500">Or sign in with email & password</span>
                        </div>
                      </div>
                    </div>
                  )}

                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input
                          id="signin-email"
                          type="email"
                          placeholder="you@company.com"
                          value={email}
                          onChange={(e) => {
                            const value = e.target.value;
                            setEmail(value);
                            checkForPasskeys(value);
                          }}
                          className="pl-10"
                          aria-busy={checkingPasskeys}
                          required
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="signin-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input
                          id="signin-password"
                          type="password"
                          placeholder="Enter your password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10"
                          required={!hasPasskeys}
                          disabled={loading || requires2FA}
                        />
                      </div>
                    </div>

                    {requires2FA && (
                      <div className="space-y-2">
                        <Label htmlFor="signin-2fa">Two-Factor Authentication Code</Label>
                        <Input
                          id="signin-2fa"
                          type="text"
                          maxLength={6}
                          placeholder="000000"
                          value={twoFactorCode}
                          onChange={(e) => setTwoFactorCode(e.target.value.replaceAll(/\D/g, ''))}
                          className="text-center text-2xl tracking-widest font-mono"
                          required
                          disabled={loading}
                          autoFocus
                        />
                        <p className="text-sm text-slate-500 text-center">
                          Enter the 6-digit code from your authenticator app
                        </p>
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          Signing in...
                        </>
                      ) : (
                        'Sign In'
                      )}
                    </Button>
                  </form>
                </div>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register">
                <div className="space-y-4">
                  <div className="text-center mb-6">
                    <h2 className="text-2xl font-bold text-foreground">{isActivation ? 'Activate Your Account' : 'Create Account'}</h2>
                    <p className="text-muted-foreground mt-1">
                      {isActivation ? 'Set your password to continue enrollment' : 'Get started with your benefits'}
                    </p>
                  </div>

                  <form onSubmit={handleRegister} className="space-y-4">
                    {error && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                      </Alert>
                    )}

                    {!isActivation && (
                      <div className="space-y-2">
                        <Label htmlFor="register-name">Full Name</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                          <Input
                            id="register-name"
                            type="text"
                            placeholder="John Doe"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            className="pl-10"
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="register-email">Email</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-email"
                          type="email"
                          placeholder="you@company.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="pl-10"
                          required
                          disabled={loading || isActivation}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="register-password">Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
                        <Input
                          id="register-password"
                          type="password"
                          placeholder={isActivation ? 'Set your password to continue' : 'Create a secure password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="pl-10"
                          required
                          disabled={loading}
                          minLength={6}
                        />
                      </div>
                      <p className="text-xs text-slate-500">At least 6 characters</p>
                    </div>

                    <Button
                      type="submit"
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                          {registerLoading}
                        </>
                      ) : (
                        registerCta
                      )}
                    </Button>

                    <p className="text-xs text-center text-slate-500 mt-4">
                      After creating your account, you'll choose whether you're a company or employee
                    </p>
                  </form>
                </div>
              </TabsContent>
            </Tabs>

            <div className="mt-6 text-center text-xs text-slate-500">
              By continuing, you agree to our Terms of Service and Privacy Policy
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
